import { BittsButtonProps } from '@crossbeam/bitts';
import { EVENT_SITES, TEAM_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';

import useBilling from '@/composables/useBilling';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import { useBillingStore } from '@/stores';

export default function useTeamUpsellCTAs() {
  const { isSupernodeTier, hasSubscription, isNotPaying } =
    storeToRefs(useBillingStore());

  const { handleBillingInteraction, hasPPV5 } = useBilling();

  const { isTrueConnector } = useConnectorTrialEndDate();

  function getTalkToSalesTooltipText(isSales: boolean) {
    if (isSupernodeTier.value) {
      const seatType = isSales ? 'Sales' : 'Full Access';
      return `You are using all available ${seatType} seats in your current contract. Talk to Sales to add more seats`;
    }
    if (isSales && hasPPV5.value) {
      return 'Upgrade to Supernode unlock access to Sales seats';
    }
    return 'Unblock your team by adding more seats to Crossbeam';
  }
  const billingInteractionBase = {
    cta: TEAM_CTAS.MORE_SEATS,
    event_site: EVENT_SITES.TEAM_COUNTER,
  };
  function openAddSeatsModal(emit: (event: string) => void) {
    handleBillingInteraction(billingInteractionBase, null, true, null, true);
    emit('open-add-seats-modal');
  }
  function getButtonInfo(
    isSales: boolean,
    isAtLimit: boolean,
    emit: (event: string) => void,
  ): BittsButtonProps & {
    tooltipText?: string;
    click?: () => void;
  } {
    if (
      (isSales &&
        (isNotPaying.value || isTrueConnector.value) &&
        hasPPV5.value) ||
      (isTrueConnector.value && !hasSubscription.value && isAtLimit) ||
      (isSupernodeTier.value && isAtLimit)
    ) {
      return {
        type: 'upsell',
        text: 'Talk to Sales',
        leftIcon: ['fak', 'upgrade'],
        tooltipText: getTalkToSalesTooltipText(isSales),
        click: () => {
          handleBillingInteraction({
            ...billingInteractionBase,
            talkToSalesReason: `More ${isSales ? 'Sales' : 'Full Access'} Seats`,
          });
        },
      };
    } else if (
      (isNotPaying.value && isAtLimit) ||
      (isNotPaying.value && isSales && !hasPPV5.value)
    ) {
      return {
        type: 'upsell',
        text: 'Upgrade',
        leftIcon: ['fak', 'upgrade'],
        tooltipText: 'Unblock your team by adding more seats to Crossbeam',
        click: () => {
          handleBillingInteraction(billingInteractionBase);
        },
      };
    } else if (isTrueConnector.value && hasSubscription.value) {
      return {
        variant: 'outline',
        leftIcon: ['fak', 'add'],
        text: 'Add seats',
        click: () => {
          hasPPV5.value
            ? openAddSeatsModal(emit)
            : handleBillingInteraction(billingInteractionBase, null, true);
        },
      };
    }
    return {};
  }
  return { getButtonInfo };
}
